import gql from "graphql-tag";
import {getImbalances} from "../../graphql/queries";
import {submitManualAdjustments} from "../../graphql/mutations";
import {AppsyncService} from "./AppsyncService";
import {oktaAuth} from "../config/OktaConfig";
import {ImbalanceItem, StatusFilter} from "../../common/data/types";
import {ManualAdjustmentInput} from "../../common/data/adjustmentMapper";
import {Imbalance, ImbalanceFilter} from "../../graphql/audit-overview-api/api";

type ImbalancePage = {
    items: ImbalanceItem[];
    nextToken: string | null;
    totalCount: number | null;
};

function toImbalanceItem(raw: Imbalance): ImbalanceItem {
    return {
        material: raw.material ?? '',
        size: raw.size ?? '',
        quality: raw.quality ?? '',
        coo: raw.coo ?? '',
        wmsQty: raw.wmsQty ?? 0,
        ciBondedQty: raw.ciBondedQty ?? 0,
        ciNonBondedQty: raw.ciNonBondedQty ?? 0,
        ciTotalQty: raw.ciTotalQty ?? 0,
        imbalance: raw.imbalance ?? 0,
        persistentImbalance: raw.persistentImbalance ?? 0,
        direction: raw.direction ?? '',
        cleanStatus: raw.cleanStatus ?? '',
        flowInformation: raw.flowInformation ?? '',
    };
}

class ImbalanceService {

    private appsyncService: AppsyncService;

    constructor() {
        this.appsyncService = new AppsyncService(
            process.env.REACT_APP_APPSYNC_AUDIT_OVERVIEW_ENDPOINT!,
            oktaAuth
        );
    }

    async fetchImbalances(
        month: string,
        material?: string,
        size?: string,
        isPersistent?: boolean,
        filter?: ImbalanceFilter,
        excludeColors?: boolean,
        statusFilter?: StatusFilter,
        nextToken?: string,
    ): Promise<ImbalancePage> {
        const mappedStatus = statusFilter === 'open' ? 'OPEN'
            : statusFilter === 'submitted' ? 'SUBMITTED'
            : null;

        const result: any = await this.appsyncService.appSyncClient.query({
            query: gql(getImbalances),
            fetchPolicy: 'network-only',
            variables: {
                month,
                material: material || null,
                size: size || null,
                isPersistent: isPersistent ?? null,
                filter: filter ?? null,
                excludeColors: excludeColors ?? null,
                statusFilter: mappedStatus,
                nextToken: nextToken ?? null,
            },
        });

        const connection = result?.data?.getImbalances;
        if (!connection?.items) return {items: [], nextToken: null, totalCount: null};

        const items = connection.items
            .filter((raw: any) => raw != null)
            .map((raw: any) => toImbalanceItem(raw));

        return {items, nextToken: connection.nextToken || null, totalCount: connection.totalCount ?? null};
    }

    async submitManualAdjustments(adjustments: ManualAdjustmentInput[]): Promise<{success: boolean; message: string}> {
        const result: any = await this.appsyncService.appSyncClient.mutate({
            mutation: gql(submitManualAdjustments),
            variables: {adjustments},
        });

        const response = result?.data?.submitManualAdjustments;
        return {
            success: response?.success ?? false,
            message: response?.message ?? '',
        };
    }

    async submitManualAdjustmentsBatched(
        adjustments: ManualAdjustmentInput[],
        onProgress: (completedItems: number, totalItems: number) => void,
    ): Promise<{failedBatches: {batchIndex: number; startIdx: number; endIdx: number; error: string}[]}> {
        const BATCH_SIZE = 100;
        const total = adjustments.length;

        const batches: {batchIndex: number; startIdx: number; items: ManualAdjustmentInput[]}[] = [];
        for (let i = 0; i < total; i += BATCH_SIZE) {
            batches.push({batchIndex: batches.length, startIdx: i, items: adjustments.slice(i, i + BATCH_SIZE)});
        }

        const failedBatches: {batchIndex: number; startIdx: number; endIdx: number; error: string}[] = [];
        let completedCount = 0;

        for (const entry of batches) {
            try {
                const {success, message} = await this.submitManualAdjustments(entry.items);
                if (!success) {
                    console.error(
                        `[ImbalanceService] Batch ${entry.batchIndex + 1} rejected — API responded with success=false`,
                        {message, items: entry.items},
                    );
                    throw new Error(message || 'Adjustment rejected by API (no message returned)');
                }
            } catch (err) {
                const msg = err instanceof Error ? err.message : String(err);
                console.error(
                    `[ImbalanceService] Batch ${entry.batchIndex + 1} failed (items ${entry.startIdx + 1}–${entry.startIdx + entry.items.length}):`,
                    msg,
                );
                failedBatches.push({
                    batchIndex: entry.batchIndex,
                    startIdx: entry.startIdx,
                    endIdx: entry.startIdx + entry.items.length,
                    error: msg,
                });
            }
            completedCount += entry.items.length;
            onProgress(completedCount, total);
        }

        return {failedBatches};
    }
}

export const imbalanceService = new ImbalanceService();
